import Banner from '@ben/orion/banner';
import Button from '@ben/orion/button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { useState } from 'react';
import type { Control } from 'react-hook-form';
import styled, { css } from 'styled-components';
import type { StepsFields } from '../elements/types';
import useLifeEventRestrictions from './hooks/use-life-event-restrictions';
import RestrictionStep from './restriction-step';

interface LifeEventRestrictionsProps {
  stepsControl: Control<StepsFields>;
  lifeEventIndex: number;
}

const LifeEventRestrictions = ({
  stepsControl,
  lifeEventIndex,
}: LifeEventRestrictionsProps) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());

  const existingSteps = useLifeEventRestrictions(stepsControl);

  const handleToggle = (index: number) => {
    setExpandedSteps((previous) => {
      const newSet = new Set(previous);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const handleExpandAll = () => {
    setExpandedSteps(new Set(existingSteps.map((_, index) => index)));
  };

  const handleCollapseAll = () => {
    setExpandedSteps(new Set());
  };

  const hasExpanded = expandedSteps.size > 0;

  return (
    <Wrapper>
      <Header>
        <HeaderContainer>
          <Typography variant={TypographyVariant.h3}>Restrictions</Typography>
          <Typography variant={TypographyVariant.bodyMd1}>
            To be applied during this life event window. Selections on step
            components can be entirely disabled by selecting the ‘enabled’
            toggle.
          </Typography>
        </HeaderContainer>
        <Button
          variant="text"
          onClick={hasExpanded ? handleCollapseAll : handleExpandAll}
        >
          {hasExpanded ? 'Collapse all' : 'Expand all'}
        </Button>
      </Header>
      <Banner
        variant="information"
        message="Save benefit to see latest updates on step components."
      />
      {existingSteps.map((step, index) => (
        <RestrictionStep
          key={step.stepId}
          index={index}
          stepId={step.stepId}
          lifeEventIndex={lifeEventIndex}
          isExpanded={expandedSteps.has(index)}
          onToggle={handleToggle}
          components={step.components}
        />
      ))}
    </Wrapper>
  );
};

export default LifeEventRestrictions;

const Wrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const Header = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `}
`;

const HeaderContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    max-width: 80%;
  `}
`;
